import { getStoredIdToken } from '../../../authToken';
import { config } from '../../../config/config';

const NODE_API_URL = (
  process.env.REACT_APP_NODE_API_URL ||
  config.node_api_url ||
  ''
).replace(/\/+$/, '');

export type SyncVerificationFieldKey =
  | 'production_id'
  | 'barcode'
  | 'seat'
  | 'marketplaces'
  | 'shared'
  | 'price';

export type SyncVerificationLogRow = {
  id: string;
  field: string;
  fieldLabel: string;
  status: 'Success' | 'Failure';
  qaValue: string;
  vlValue: string;
  systemsNote: string | null;
  failureReason: string | null;
  authoritativeSystem: string | null;
  triggerSource: string | null;
  vlAction: string | null;
  createdAt: string | null;
  eventName: string | null;
  eventDate: string | null;
  venue: string | null;
  section: string | null;
  row: string | null;
  seat: string | number | null;
  seatLabel: string | null;
  tpId: string | null;
  tpStatus: string | null;
  clientName: string | null;
  clientEmail: string | null;
  verifiedByName: string | null;
};

export type SyncVerificationLogsResponse = {
  logs: SyncVerificationLogRow[];
  totalCount: number;
  limit: number;
  offset: number;
  message?: string;
};

export type VerifyVlSyncResponse = {
  message: string;
  successCount: number;
  failureCount: number;
  unmatchedCount: number;
  verifiedPlacementCount: number;
  results: Array<{
    tpId: string;
    eventName: string | null;
    seatLabel: string;
    fieldResults: Array<{
      field: SyncVerificationFieldKey;
      status: 'Success' | 'Failure';
      qaValue: string;
      vlValue: string;
      systemsNote: string;
      failureReason: string | null;
      authoritativeSystem: string | null;
    }>;
  }>;
};

export async function fetchSyncVerificationLogs(params: {
  limit?: number;
  offset?: number;
  search?: string;
  field?: string;
  status?: string;
  tpId?: string;
  tpIds?: string[];
}): Promise<SyncVerificationLogsResponse> {
  const token = getStoredIdToken();
  const apiUrl = `${NODE_API_URL}/api/listSyncVerificationLogs`;
  if (!NODE_API_URL) {
    throw new Error(
      'Backend API URL is not configured. Set REACT_APP_NODE_API_URL=http://localhost:4000 in .env.local',
    );
  }
  let response: Response;
  try {
    response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
      search: params.search || '',
      field: params.field || '',
      status: params.status || '',
      ...(params.tpId ? { tpId: params.tpId } : {}),
      ...(params.tpIds?.length ? { tpIds: params.tpIds } : {}),
    }),
  });
  } catch (err: any) {
    throw new Error(
      err?.message === 'Failed to fetch'
        ? `Backend API unreachable at ${NODE_API_URL}. Ensure Node is running (npm run start:local in Quickasyst-node-backend).`
        : err?.message || 'Failed to load sync verification logs',
    );
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || 'Failed to load sync verification logs');
  }
  return data as SyncVerificationLogsResponse;
}

export async function runSyncVerification(params: {
  ticketPlacementIds?: string[];
  eventId?: string;
  fields?: SyncVerificationFieldKey[];
  verifyAllListed?: boolean;
}): Promise<VerifyVlSyncResponse> {
  const token = getStoredIdToken();
  if (!NODE_API_URL) {
    throw new Error(
      'Backend API URL is not configured. Set REACT_APP_NODE_API_URL=http://localhost:4000 in .env.local',
    );
  }
  let response: Response;
  try {
    response = await fetch(`${NODE_API_URL}/api/verifyVlSync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      ticketPlacementIds: params.ticketPlacementIds ?? [],
      ...(params.eventId ? { eventId: params.eventId } : {}),
      ...(params.fields?.length ? { fields: params.fields } : {}),
      ...(params.verifyAllListed != null ? { verifyAllListed: params.verifyAllListed } : {}),
      triggerSource: 'manual',
    }),
  });
  } catch (err: any) {
    throw new Error(
      err?.message === 'Failed to fetch'
        ? `Backend API unreachable at ${NODE_API_URL}. Ensure Node is running (npm run start:local in Quickasyst-node-backend).`
        : err?.message || 'Sync verification failed',
    );
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || 'Sync verification failed');
  }
  return data as VerifyVlSyncResponse;
}
